import { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import Spinner from "./Spinner";

type Values = Record<string, string | number | null>;

interface AuditEntry {
  id: string;
  action: "Added" | "Updated" | "Deleted" | string;
  transaction_id: string;
  partner: string | null;
  email: string | null;
  amount: number | null;
  date_of_deposit: string | null;
  by: string;
  at: string;
  before: Values | null;
  after: Values | null;
  changed_fields: string[];
}

const LABELS: Record<string, string> = {
  "Date of Deposit": "Date",
  "Deposit Type": "Type",
  "Valuation Status": "Status",
  "Cash/Account Transfer Amount": "Amount",
  "% benefits": "Benefit",
  "Portfolio Value at the Date of Valuation": "Portfolio value",
  "SSN Fund Value (Per Txn)": "Unit price",
  "SSN Units Bought (Per Txn)": "Units",
};

const ACTION_COLORS: Record<string, React.CSSProperties> = {
  Added: { background: "rgba(29,158,117,0.15)", color: "var(--teal-light)" },
  Updated: { background: "rgba(230,180,80,0.15)", color: "#e6c06a" },
  Deleted: { background: "rgba(224,90,90,0.15)", color: "#f08a8a" },
};

function money(n: number | null | undefined) {
  if (n === null || n === undefined) return "-";
  return `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function show(field: string, v: string | number | null | undefined) {
  if (v === null || v === undefined || v === "") return "blank";
  if (typeof v !== "number") return String(v);
  if (field === "% benefits") return `${+(v * 100).toFixed(4)}%`;
  if (field === "Cash/Account Transfer Amount" || field === "Portfolio Value at the Date of Valuation") return money(v);
  return v.toLocaleString("en-US", { maximumFractionDigits: 4 });
}

function when(iso: string) {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

export default function AuditLog() {
  const [entries, setEntries] = useState<AuditEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const [action, setAction] = useState("");

  const load = useCallback(async () => {
    setError(null);
    try {
      const { data } = await api.get<AuditEntry[]>("/transactions/audit");
      setEntries(data);
    } catch (e: any) {
      setEntries([]);
      setError(e?.response?.data?.detail ?? "Failed to load the audit log");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const q = filter.trim().toLowerCase();
  const visible = (entries ?? []).filter((e) =>
    (!action || e.action === action) &&
    (!q || `${e.partner} ${e.email} ${e.by} ${e.transaction_id} ${e.date_of_deposit}`.toLowerCase().includes(q)),
  );

  const changes = (e: AuditEntry) => {
    if (e.action === "Updated") {
      if (!e.changed_fields.length) return <span style={{ color: "var(--text-muted)" }}>Saved with no changes</span>;
      return e.changed_fields.map((f) => (
        <div key={f}>
          <span style={{ color: "var(--text-muted)" }}>{LABELS[f] ?? f}:</span>{" "}
          <span style={S.old}>{show(f, e.before?.[f])}</span> → {show(f, e.after?.[f])}
        </div>
      ));
    }
    const v = e.after ?? e.before ?? {};
    return (
      <span style={{ color: "var(--text-secondary)" }}>
        {[v["Action"], v["Deposit Type"], v["Valuation Status"]].filter(Boolean).join(" · ")}
        {v["SSN Fund Value (Per Txn)"] ? ` · unit price ${show("SSN Fund Value (Per Txn)", v["SSN Fund Value (Per Txn)"])}` : ""}
      </span>
    );
  };

  return (
    <div style={S.card}>
      <div style={S.header}>
        <h3 style={S.title}>Transaction audit log {entries && <span style={S.count}>({visible.length})</span>}</h3>
        <div style={S.headerActions}>
          <select style={{ ...S.search, minWidth: 0 }} value={action} onChange={(e) => setAction(e.target.value)} aria-label="Action">
            <option value="">All actions</option>
            <option>Added</option>
            <option>Updated</option>
            <option>Deleted</option>
          </select>
          <input style={S.search} placeholder="Filter by partner, admin or date"
            value={filter} onChange={(e) => setFilter(e.target.value)} />
          <button style={S.secondaryBtn} onClick={() => { setEntries(null); load(); }}>Refresh</button>
        </div>
      </div>

      {error && <pre style={S.error}>{error}</pre>}

      {entries === null ? <Spinner /> : (
        <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          <table style={{ ...S.table, minWidth: 900 }}>
            <thead>
              <tr>{["When", "Action", "Transaction", "By", "Details"].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {visible.map((e, i) => (
                <tr key={e.id} style={i % 2 ? { background: "rgba(255,255,255,0.02)" } : {}}>
                  <td style={{ ...S.td, color: "var(--text-secondary)", verticalAlign: "top" }}>{when(e.at)}</td>
                  <td style={{ ...S.td, verticalAlign: "top" }}>
                    <span style={{ ...S.badge, ...(ACTION_COLORS[e.action] ?? {}) }}>{e.action}</span>
                  </td>
                  <td style={{ ...S.td, verticalAlign: "top" }}>
                    <div style={{ fontWeight: 500 }}>{e.partner ?? "-"}</div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
                      #{e.transaction_id} · {e.date_of_deposit ?? "-"} · {money(e.amount)}
                    </div>
                  </td>
                  <td style={{ ...S.td, color: "var(--text-secondary)", verticalAlign: "top" }}>{e.by || "-"}</td>
                  <td style={{ ...S.td, ...S.change }}>{changes(e)}</td>
                </tr>
              ))}
              {entries.length > 0 && visible.length === 0 && (
                <tr><td style={{ ...S.td, color: "var(--text-muted)" }} colSpan={5}>No entries match.</td></tr>
              )}
              {entries.length === 0 && !error && (
                <tr><td style={{ ...S.td, color: "var(--text-muted)" }} colSpan={5}>
                  No changes recorded yet. Every transaction added, edited or deleted from now on appears here.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const S: Record<string, React.CSSProperties> = {
  card: {
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 12, padding: "24px 20px",
    boxSizing: "border-box",
  },
  header: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    gap: 12, flexWrap: "wrap", marginBottom: 16,
  },
  headerActions: { display: "flex", gap: 12, flexWrap: "wrap" },
  search: {
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 8, padding: "8px 12px",
    color: "var(--text-primary)", fontSize: 13, minWidth: 220,
  },
  badge: { display: "inline-block", padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 600 },
  change: { fontSize: 12, lineHeight: 1.6, whiteSpace: "normal", minWidth: 260 },
  old: { color: "#f08a8a", textDecoration: "line-through" },
  title: { fontSize: 14, fontWeight: 600, color: "var(--text-primary)" },
  count: { color: "var(--text-muted)", fontWeight: 400 },
  form: {
    display: "grid", gap: 16, marginBottom: 20, padding: 16, borderRadius: 8,
    background: "rgba(255,255,255,0.03)",
    gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
  },
  field: { display: "flex", flexDirection: "column", gap: 6 },
  label: {
    fontSize: 11, fontWeight: 500, letterSpacing: "0.08em",
    textTransform: "uppercase", color: "var(--text-muted)",
  },
  input: {
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 8, padding: "10px 12px",
    color: "var(--text-primary)", fontSize: 14, width: "100%",
  },
  readOnly: { opacity: 0.6, cursor: "not-allowed" },
  formActions: { display: "flex", gap: 12, alignItems: "end", flexWrap: "wrap" },
  hint: { gridColumn: "1 / -1", fontSize: 12, color: "var(--text-muted)" },
  primaryBtn: {
    padding: "9px 16px", borderRadius: 8, fontSize: 13, fontWeight: 600,
    background: "var(--teal)", color: "#fff",
  },
  secondaryBtn: {
    padding: "9px 16px", borderRadius: 8, fontSize: 13, fontWeight: 500,
    border: "1px solid rgba(255,255,255,0.2)", color: "var(--text-primary)",
  },
  disabled: { opacity: 0.4, cursor: "not-allowed" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 560 },
  th: {
    textAlign: "left", padding: "10px 12px",
    color: "var(--text-muted)", fontSize: 11, fontWeight: 500,
    letterSpacing: "0.08em", textTransform: "uppercase",
    borderBottom: "1px solid rgba(255,255,255,0.07)", whiteSpace: "nowrap",
  },
  td: {
    padding: "10px 12px", color: "var(--text-primary)",
    borderBottom: "1px solid rgba(255,255,255,0.04)", whiteSpace: "nowrap",
  },
  rowBtn: {
    padding: "5px 10px", borderRadius: 6, fontSize: 12, fontWeight: 500,
    border: "1px solid rgba(255,255,255,0.15)", color: "var(--text-primary)", marginLeft: 6,
  },
  deleteBtn: { borderColor: "rgba(224,90,90,0.4)", color: "#f08a8a" },
  success: {
    marginBottom: 16, padding: "10px 12px", borderRadius: 8,
    background: "rgba(29,158,117,0.12)", border: "1px solid var(--teal-dim)",
    color: "var(--teal-light)", fontSize: 13,
  },
  error: {
    marginBottom: 16, padding: "10px 12px", borderRadius: 8,
    background: "rgba(224,90,90,0.12)", border: "1px solid rgba(224,90,90,0.4)",
    color: "#f08a8a", fontSize: 13, fontFamily: "var(--font-body)", whiteSpace: "pre-wrap",
  },
};
